import type { LookId } from './types';

export interface Look {
  id: LookId;
  name: string;
  description: string;
  fontsHref: string; // Google Fonts stylesheet; clients that ignore it fall back to the stacks below
  headFont: string;
  bodyFont: string;
  headWeight: number;
  headTracking: string;
  outer: string; // page background around the email
  paper: string; // email background
  text: string;
  heading: string;
  muted: string;
  accent: string; // small labels, timeline labels
  line: string;
  buttonBg: string;
  buttonFg: string;
  buttonRadius: number;
  buttonFull: boolean; // full-width button (One Thing)
  darkBg: string; // offer box
  darkFg: string;
  darkMuted: string;
  darkButtonBg: string;
  darkButtonFg: string;
  cardBg: string; // price list / offer card background
  header: 'left' | 'center' | 'masthead';
  logo: 'dark' | 'white';
  footerBg: string;
  footerFg: string;
  timeline: 'rail' | 'table' | 'list';
  heroH1: number; // px, desktop
  h2: number;
  radius: number; // photo corners
  pad: number; // side padding
}

const LOOKS: Record<LookId, Look> = {
  countdown: {
    id: 'countdown',
    name: 'Countdown',
    description: 'Clean and calm. Spa blue buttons and a timeline with dots.',
    fontsHref: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap',
    headFont: "Poppins, 'Helvetica Neue', Arial, sans-serif",
    bodyFont: "Poppins, 'Helvetica Neue', Arial, sans-serif",
    headWeight: 600,
    headTracking: '-0.01em',
    outer: '#eef3f5',
    paper: '#ffffff',
    text: '#1f2b31',
    heading: '#1f2b31',
    muted: '#4a5a61',
    accent: '#3e5560',
    line: '#e3eaed',
    buttonBg: '#a3c4d3',
    buttonFg: '#10202a',
    buttonRadius: 8,
    buttonFull: false,
    darkBg: '#3e5560',
    darkFg: '#ffffff',
    darkMuted: '#d5e2e8',
    darkButtonBg: '#ffffff',
    darkButtonFg: '#10202a',
    cardBg: '#f3f7f9',
    header: 'left',
    logo: 'dark',
    footerBg: '#ffffff',
    footerFg: '#7a888e',
    timeline: 'rail',
    heroH1: 34,
    h2: 24,
    radius: 14,
    pad: 28,
  },
  journal: {
    id: 'journal',
    name: 'Journal',
    description: 'Reads like a magazine letter. Serif headlines, thin rules.',
    fontsHref: 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500&family=Source+Sans+3:wght@400;600&display=swap',
    headFont: "Fraunces, Georgia, 'Times New Roman', serif",
    bodyFont: "'Source Sans 3', 'Helvetica Neue', Arial, sans-serif",
    headWeight: 400,
    headTracking: '-0.01em',
    outer: '#e9e6e2',
    paper: '#ffffff',
    text: '#38322d',
    heading: '#1d1a17',
    muted: '#6d655e',
    accent: '#8a6f5c',
    line: '#e3ddd6',
    buttonBg: '#1d1a17',
    buttonFg: '#ffffff',
    buttonRadius: 2,
    buttonFull: false,
    darkBg: '#1d1a17',
    darkFg: '#f2eee9',
    darkMuted: '#c9c1b8',
    darkButtonBg: '#f2eee9',
    darkButtonFg: '#1d1a17',
    cardBg: '#f6f3ef',
    header: 'masthead',
    logo: 'dark',
    footerBg: '#ffffff',
    footerFg: '#8a8079',
    timeline: 'table',
    heroH1: 34,
    h2: 28,
    radius: 0,
    pad: 32,
  },
  afterdark: {
    id: 'afterdark',
    name: 'After Dark',
    description: 'Dark and dressy, with champagne gold. Good for holidays and gift cards.',
    fontsHref: 'https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Instrument+Sans:wght@400;600&display=swap',
    headFont: "'DM Serif Display', Georgia, 'Times New Roman', serif",
    bodyFont: "'Instrument Sans', 'Helvetica Neue', Arial, sans-serif",
    headWeight: 400,
    headTracking: '0',
    outer: '#0b1116',
    paper: '#121b22',
    text: '#d9d3c9',
    heading: '#ffffff',
    muted: '#b9b2a6',
    accent: '#d8c3a2',
    line: '#2a3640',
    buttonBg: '#d8c3a2',
    buttonFg: '#121b22',
    buttonRadius: 4,
    buttonFull: false,
    darkBg: '#1b2730',
    darkFg: '#ffffff',
    darkMuted: '#b9b2a6',
    darkButtonBg: '#d8c3a2',
    darkButtonFg: '#121b22',
    cardBg: '#18232b',
    header: 'center',
    logo: 'white',
    footerBg: '#121b22',
    footerFg: '#6f7b83',
    timeline: 'list',
    heroH1: 40,
    h2: 28,
    radius: 6,
    pad: 32,
  },
  menu: {
    id: 'menu',
    name: 'Menu',
    description: 'Warm blush, big serif titles. Made for price lists and specials.',
    fontsHref: 'https://fonts.googleapis.com/css2?family=Instrument+Serif&family=Figtree:wght@400;600;700&display=swap',
    headFont: "'Instrument Serif', Georgia, 'Times New Roman', serif",
    bodyFont: "Figtree, 'Helvetica Neue', Arial, sans-serif",
    headWeight: 400,
    headTracking: '-0.01em',
    outer: '#ead3cc',
    paper: '#f3e1db',
    text: '#4b3834',
    heading: '#231816',
    muted: '#6b524d',
    accent: '#a0675c',
    line: '#d4b6ae',
    buttonBg: '#231816',
    buttonFg: '#ffffff',
    buttonRadius: 4,
    buttonFull: false,
    darkBg: '#231816',
    darkFg: '#f3e1db',
    darkMuted: '#cdb3ac',
    darkButtonBg: '#f3e1db',
    darkButtonFg: '#231816',
    cardBg: '#ffffff',
    header: 'center',
    logo: 'dark',
    footerBg: '#231816',
    footerFg: '#b9a29d',
    timeline: 'list',
    heroH1: 52,
    h2: 30,
    radius: 4,
    pad: 24,
  },
  onething: {
    id: 'onething',
    name: 'One Thing',
    description: 'One big photo, one message, one button. The fastest to read.',
    fontsHref: 'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;600;700&display=swap',
    headFont: "'Inter Tight', 'Helvetica Neue', Arial, sans-serif",
    bodyFont: "'Inter Tight', 'Helvetica Neue', Arial, sans-serif",
    headWeight: 700,
    headTracking: '-0.03em',
    outer: '#dfe3e5',
    paper: '#ffffff',
    text: '#111111',
    heading: '#111111',
    muted: '#555555',
    accent: '#111111',
    line: '#e2e2e2',
    buttonBg: '#111111',
    buttonFg: '#ffffff',
    buttonRadius: 6,
    buttonFull: true,
    darkBg: '#111111',
    darkFg: '#ffffff',
    darkMuted: '#bbbbbb',
    darkButtonBg: '#ffffff',
    darkButtonFg: '#111111',
    cardBg: '#f4f4f4',
    header: 'center',
    logo: 'dark',
    footerBg: '#ffffff',
    footerFg: '#888888',
    timeline: 'list',
    heroH1: 52,
    h2: 26,
    radius: 0,
    pad: 28,
  },
};

export const LOOK_IDS = Object.keys(LOOKS) as LookId[];

export function getLook(id: string): Look {
  return LOOKS[id as LookId] ?? LOOKS.countdown;
}

export function isLookId(id: unknown): id is LookId {
  return typeof id === 'string' && id in LOOKS;
}
